/**
 * HubSpot Adapter - Submit form data to HubSpot Forms API
 *
 * This adapter formats the payload for HubSpot's Forms API v3.
 * The consumer website loads the HubSpot tracking script separately.
 *
 * @example
 * const adapter = createHubSpotAdapter({
 *   portalId: '12345678',
 *   formId: 'abc-123-def',
 *   fieldMapping: { fullName: 'firstname' }
 * });
 * await adapter.submit(formData, context);
 */

import type {
  FormData,
  SubmissionContext,
  SubmissionResult,
  SubmissionAdapter,
  HubSpotConfig,
  HubSpotSubmissionPayload,
  HubSpotField,
} from './types.js';

/**
 * Get HubSpot tracking cookie (hutk)
 */
export function getHubSpotCookie(): string | undefined {
  if (typeof document === 'undefined') {return undefined;}

  const match = document.cookie.match(/hubspotutk=([^;]+)/);
  return match ? match[1] : undefined;
}

/**
 * Create a HubSpot submission adapter
 */
export function createHubSpotAdapter(config: HubSpotConfig): SubmissionAdapter {
  const { portalId, formId, fieldMapping = {}, mock = false } = config;

  return {
    name: 'hubspot',

    mapFields(data: FormData): FormData {
      const mapped: FormData = {};
      for (const [key, value] of Object.entries(data)) {
        const mappedKey = fieldMapping[key] || key;
        mapped[mappedKey] = value;
      }
      return mapped;
    },

    async submit(data: FormData, context: SubmissionContext): Promise<SubmissionResult> {
      // Mock mode for testing
      if (mock) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: true, data: { mock: true, formData: data } };
      }

      const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

      // Map fields to HubSpot format
      const mappedData = this.mapFields(data);
      const fields: HubSpotField[] = Object.entries(mappedData).map(([name, value]) => ({
        name,
        value: Array.isArray(value) ? value.join(';') : String(value ?? ''),
      }));

      const payload: HubSpotSubmissionPayload = {
        fields,
        context: {
          pageUri: context.pageUrl,
          pageName: context.pageTitle,
          hutk: getHubSpotCookie(),
        },
      };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.message || `HubSpot submission failed: ${response.status}`;
          return { success: false, error: errorMessage };
        }

        const responseData = await response.json();
        return { success: true, data: responseData };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Network error';
        return { success: false, error: errorMessage };
      }
    },
  };
}

export type { HubSpotConfig };
